import type { OnboardingState, OnboardingStatus, OnboardingStep } from "./types";

const ONBOARDING_STEP_ORDER = [
  "welcome",
  "tap",
  "map",
  "workshop",
  "upgrade",
  "passive",
  "trophy",
  "trophy-overview",
  "season",
  "season-overview",
  "season-return",
  "club",
  "club-overview",
  "club-link",
  "club-return",
  "arena",
  "arena-overview",
  "arena-game",
  "arena-return",
  "complete",
] as const satisfies readonly OnboardingStep[];

const ONBOARDING_STEP_RANK = new Map<OnboardingStep, number>(
  ONBOARDING_STEP_ORDER.map((step, index) => [step, index]),
);

function statusRank(status: OnboardingStatus): number {
  if (status === "not-started") return 0;
  if (status === "in-progress") return 1;
  return 2;
}

export function isOnboardingActive(onboarding: OnboardingState): boolean {
  return onboarding.status === "not-started" || onboarding.status === "in-progress";
}

/**
 * Onboarding is a forward-only route inside one published version. A compact
 * tap delta can carry a newer account revision while still retaining the
 * previous client-side onboarding step. If the full command response then
 * arrives out of order, keep the newer account snapshot but accept only its
 * forward onboarding progress.
 */
export function reconcileOnboardingProgress(
  current: OnboardingState,
  incoming: OnboardingState,
): OnboardingState {
  if (incoming.version !== current.version) return current;

  const currentStatusRank = statusRank(current.status);
  const incomingStatusRank = statusRank(incoming.status);
  if (incomingStatusRank > currentStatusRank) return incoming;
  if (incomingStatusRank < currentStatusRank) return current;

  if (current.status === "completed" || current.status === "skipped") return current;

  const currentStepRank = ONBOARDING_STEP_RANK.get(current.step) ?? -1;
  const incomingStepRank = ONBOARDING_STEP_RANK.get(incoming.step) ?? -1;
  return incomingStepRank > currentStepRank ? incoming : current;
}
